import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Search,
  Briefcase,
  Clock,
  Filter,
  DollarSign,
  ChevronDown,
  ChevronRight,
  MapPin,
  Building,
  Calendar,
} from "lucide-react";

export const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const Jobs = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    fullTime: false,
    partTime: false,
    remote: false,
  });
  const [sortBy, setSortBy] = useState("date");
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;
  
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  useEffect(() => {
    fetchJobs();
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchTerm(searchFromUrl);
    }
  }, [searchParams]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/jobs`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data.jobs)) {
        const approvedJobs = data.jobs.filter((job) => job.isApproved);
        setJobs(approvedJobs);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterKey) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
  };

  const handleSort = (sortType) => {
    setSortBy(sortType);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ search: searchTerm });
  };

  const filteredJobs = jobs.filter((job) => {
    if (searchTerm && !job.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    if (filters.fullTime && job.type !== "Full Time") return false;
    if (filters.partTime && job.type !== "Part Time") return false;
    if (filters.remote && !job.isRemote) return false;

    return true;
  });

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="text-red-500 text-xl mb-4">Error Loading Jobs</div>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('jobs.title')}</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('jobs.subtitle')}</p>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <form onSubmit={handleSearch} className="mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('jobs.searchPlaceholder')}
                    className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-orange-500 text-white px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors duration-200 font-medium"
                >
                  {t('jobs.searchButton')}
                </button>
              </div>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Job Type Filters */}
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700 flex items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Job Type
                </h3>
                {Object.entries(filters).map(([key, value]) => (
                  <label key={key} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={() => handleFilterChange(key)}
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-gray-600">{t(`jobs.filterTypes.${key}`)}</span>
                  </label>
                ))}
              </div>

              {/* Sort Options */}
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700 flex items-center">
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Sort By
                </h3>
                {Object.entries(t('jobs.sortOptions', { returnObjects: true })).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => handleSort(key)}
                    className={`w-full text-left py-2 px-3 rounded-lg transition-colors ${
                      sortBy === key
                        ? "bg-orange-100 text-orange-600"
                        : "hover:bg-gray-100 text-gray-600"
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Job Listings */}
          <div className="space-y-6">
            {currentJobs.length > 0 ? (
              currentJobs.map((job) => (
                <div
                  key={job._id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border-l-4 border-transparent hover:border-orange-500"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="bg-orange-100 p-3 rounded-lg">
                          <Building className="w-6 h-6 text-orange-500" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {job.title}
                          </h3>
                          <div className="flex flex-wrap gap-4 text-gray-600 mb-4">
                            <span className="flex items-center">
                              <Building className="w-4 h-4 mr-2" />
                              {job.company || 'Company Name'}
                            </span>
                            <span className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2" />
                              {job.location}
                            </span>
                            <span className="flex items-center">
                              <Briefcase className="w-4 h-4 mr-2" />
                              {job.type}
                            </span>
                            {job.salary && (
                              <span className="flex items-center">
                                <DollarSign className="w-4 h-4 mr-2" />
                                {job.salary}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2" />
                              Posted {formatDate(job.postedDate)}
                            </span>
                          </div>
                          <p className="text-gray-600 line-clamp-2">{job.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 md:items-end">
                      <Link
                        to={`/job/${job._id}`}
                        className="inline-flex items-center text-orange-500 hover:text-orange-600 font-medium"
                      >
                        {t('jobs.jobCard.viewDetails')}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                      <Link
                        to={`/apply/${job._id}`}
                        className="inline-flex items-center justify-center bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors duration-200 font-medium"
                      >
                        {t('jobs.jobCard.applyNow')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('jobs.noResults')}</h3>
                <p className="text-gray-600">
                  {searchTerm ? "Try adjusting your search terms." : "No jobs available at the moment."}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredJobs.length > jobsPerPage && (
            <div className="flex justify-center mt-8">
              <nav className="flex items-center space-x-2">
                {Array.from({ length: Math.ceil(filteredJobs.length / jobsPerPage) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === index + 1
                        ? "bg-orange-500 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Jobs;
